import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { QueryClientProvider } from "@tanstack/react-query";
import * as Sentry from "@sentry/react";
import App from "./App.tsx";
import PaxApp from "./pax/PaxApp.tsx";
import "./index.css";
import { ThemeProvider } from "./contexts/ThemeContext";
import "./styles/mobile.css";
import "./styles/enhanced-ui.css";
import "./styles/navigation.css";
import "./i18n"; // Initialize i18n
import { installStaleAssetRecovery } from "./utils/staleAssetRecovery";
import { scrubSentryEvent } from "./lib/observability/sentryPrivacy";
import { queryClient } from "./lib/react-query";

installStaleAssetRecovery();

// Initialize Sentry for error tracking and performance monitoring
Sentry.init({
  dsn: import.meta.env.VITE_SENTRY_DSN,
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration({
      maskAllText: true,
      maskAllInputs: true,
      blockAllMedia: true,
    }),
  ],
  // Performance Monitoring
  tracesSampleRate: import.meta.env.PROD ? 0.1 : 1.0, // 10% in production, 100% in dev
  tracePropagationTargets: ["localhost", /^https:\/\/[^/]*\.supabase\.co/],
  // Session Replay
  replaysSessionSampleRate: 0,
  replaysOnErrorSampleRate: import.meta.env.PROD ? 0.1 : 0,
  // Environment
  environment: import.meta.env.MODE,
  // Only enable if DSN is provided
  enabled: !!import.meta.env.VITE_SENTRY_DSN,
  sendDefaultPii: false,
  // Ignore common errors
  ignoreErrors: [
    // Browser extensions
    "top.GLOBALS",
    "ResizeObserver loop limit exceeded",
  ],
  beforeSend: scrubSentryEvent,
  beforeSendTransaction: scrubSentryEvent,
});

const isPaxHost = window.location.hostname.startsWith("pax.");

function RootRouter() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>{isPaxHost ? <PaxApp /> : <App />}</ThemeProvider>
    </QueryClientProvider>
  );
}

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <RootRouter />
  </StrictMode>,
);
